import { rgbRev } from './Util.js';
const finite = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};
/**
 * Resolve a gauge's value + colour stops into the shared {@link GaugeColorModel}. Returns `null` when
 * there is nothing to draw (no colour stops / no runs), matching the renderer's early-out.
 */
export function buildGaugeColorModel(element) {
    // --- Value mapping (authored Min..Max domain → 0–100 track position) ---
    const min = finite(element.min, 0);
    const max = finite(element.max, 100);
    const range = max - min;
    const norm = (v) => {
        if (range === 0)
            return 0;
        return Math.max(0, Math.min(100, ((v - min) / range) * 100));
    };
    const valuePos = norm(finite(element.value, 0));
    const originPos = norm(finite(element.origin, min));
    // Active fill interval in track-position space (0–100).
    // Non-symmetric: from the origin toward the value (handles normal bars, centre-bar, pan).
    // Symmetric: a band of length = value, centred on the origin (handles stereo-width).
    let fillStart;
    let fillEnd;
    if (element.symmetric) {
        const half = valuePos / 2;
        fillStart = Math.max(0, Math.min(100, originPos - half));
        fillEnd = Math.max(0, Math.min(100, originPos + half));
    }
    else {
        fillStart = Math.min(originPos, valuePos);
        fillEnd = Math.max(originPos, valuePos);
    }
    const hasFill = element.fillEnabled && fillEnd > fillStart;
    const trackAmount = Math.max(0, Math.min(100, finite(element.trackAmount, 0))) / 100;
    // --- Colour stops → runs between consecutive stops, with the first anchored to position 0
    //     and the last extended to 100 so the track never has an uncoloured gap. ---
    const stops = [...element.stops]
        .map((s) => ({ pos: norm(finite(s.value, 0)), color: finite(s.color, 0), gradient: !!s.gradient }))
        .sort((a, b) => a.pos - b.pos);
    if (stops.length === 0)
        return null;
    const runs = [];
    for (let i = 0; i < stops.length; i++) {
        const start = i === 0 ? 0 : stops[i].pos;
        const end = i + 1 < stops.length ? stops[i + 1].pos : 100;
        if (end <= start)
            continue;
        const gradient = stops[i].gradient && i + 1 < stops.length;
        runs.push({
            start,
            end,
            colorStart: stops[i].color,
            colorEnd: gradient ? stops[i + 1].color : stops[i].color,
            gradient,
        });
    }
    if (runs.length === 0)
        return null;
    // Single-colour fill: colour of the highest stop whose position <= value.
    let singleColor = stops[0].color;
    for (const s of stops)
        if (s.pos <= valuePos)
            singleColor = s.color;
    const lerp = (a, b, t) => a + (b - a) * t;
    const rgbaAt = (run, p) => {
        const c0 = rgbRev(run.colorStart, true);
        if (!run.gradient)
            return c0;
        const c1 = rgbRev(run.colorEnd, true);
        const span = run.end - run.start;
        const t = span > 0 ? Math.max(0, Math.min(1, (p - run.start) / span)) : 0;
        return { r: lerp(c0.r, c1.r, t), g: lerp(c0.g, c1.g, t), b: lerp(c0.b, c1.b, t), a: lerp(c0.a, c1.a, t) };
    };
    return { valuePos, fillStart, fillEnd, hasFill, trackAmount, runs, singleColor, rgbaAt };
}
//# sourceMappingURL=GaugeColorModel.js.map